import React, { useState, useRef, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';

interface QuestionInputProps {
  isConnected: boolean;
  onQuestionSubmit: (question: string) => void;
  isLoading: boolean;
}

const QuestionInput: React.FC<QuestionInputProps> = ({
  isConnected,
  onQuestionSubmit,
  isLoading
}) => {
  const [question, setQuestion] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [question]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (question.trim() && !isLoading) {
      onQuestionSubmit(question.trim());
      setQuestion('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !isLoading) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getCharacterCountColor = () => {
    const length = question.length;
    if (length > 900) return '#e74c3c';
    if (length > 800) return '#f39c12';
    return '#6c757d';
  };

  const placeholderTexts = [
    "What's the future of artificial intelligence?",
    "How does quantum computing work?",
    "What are the best practices for sustainable living?",
    "Explain the concept of blockchain technology",
    "What are the latest developments in space exploration?"
  ];

  const [currentPlaceholder, setCurrentPlaceholder] = useState(placeholderTexts[0]);

  useEffect(() => {
    if (!question && !isFocused) {
      const interval = setInterval(() => {
        setCurrentPlaceholder(prev => {
          const currentIndex = placeholderTexts.indexOf(prev);
          return placeholderTexts[(currentIndex + 1) % placeholderTexts.length];
        });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [question, isFocused]);

  // Extract complex elements for better readability
  const questionIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
      <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="currentColor"/>
      <path d="M5 6L5.5 7.5L7 8L5.5 8.5L5 10L4.5 8.5L3 8L4.5 7.5L5 6Z" fill="currentColor"/>
    </svg>
  );

  const submitIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
    </svg>
  );

  const warningIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
    </svg>
  );

  const textareaProps = {
    ref: textareaRef,
    value: question,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onKeyDown: handleKeyDown,
    placeholder: currentPlaceholder,
    className: "enhanced-question-input",
    disabled: !isConnected || isLoading,
    rows: 1,
    maxLength: 1000
  };

  const buttonProps = {
    type: "submit" as const,
    disabled: !question.trim() || !isConnected || isLoading,
    className: "enhanced-submit-button"
  };

  const containerClasses = `enhanced-question-input-container ${isFocused ? 'focused' : ''} ${isLoading ? 'loading' : ''}`;

  return (
    <div className="enhanced-question-section">
      <div className="question-header">
        <div className="question-icon">
          {questionIcon}
        </div>
        <div className="question-title-group">
          <h2 className="enhanced-question-title">Ask Your Question</h2>
          <p className="enhanced-question-subtitle">
            Explore any topic with AI-powered insights and follow-up questions
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="enhanced-question-form">
        <div className={containerClasses}>
          <div className="textarea-wrapper">
            <textarea {...textareaProps} />
            <div className="input-glow"></div>
          </div>
          
          <div className="enhanced-question-footer">
            <div className="input-hints">
              <span className="keyboard-hint">
                <kbd>⌘</kbd> + <kbd>Enter</kbd> to submit
              </span>
              <span 
                className="character-count" 
                style={{ color: getCharacterCountColor() }}
              >
                {question.length}/1000
              </span>
            </div>
            
            <button {...buttonProps}>
              {isLoading ? (
                <div className="button-loading">
                  <LoadingSpinner 
                    message="" 
                    size="small" 
                    variant="primary"
                    showMessage={false}
                  />
                  <span>Generating insights...</span>
                </div>
              ) : (
                <>
                  {submitIcon}
                  <span>Get 10 Perspectives</span>
                  <div className="button-shine"></div>
                </>
              )}
            </button>
          </div>
        </div>

        {!isConnected && (
          <div className="connection-warning">
            {warningIcon}
            Please connect your OpenAI API key to start exploring
          </div>
        )}
      </form>
    </div>
  );
};

export default QuestionInput; 